<script lang="ts">
	import type { ISbStoryData, ISbRichtext } from '@storyblok/js';
	import { editable } from '../editable';
	import type { SbColor, SbGridCol, SbGridColMobile } from '../types';
	import PageGrid from '../components/pageGrid.svelte';
	import SbRichtext from '../components/sbRichtext.svelte';

	interface Props {
		content: ISbStoryData<{
			text: ISbRichtext;
			textColor: SbColor;
			spacing: 'none' | 'my-6' | 'my-10';
			desktop_grid_start: SbGridCol;
			desktop_grid_end: SbGridCol;
			mobile_grid_start: SbGridColMobile;
			mobile_grid_end: SbGridColMobile;
		}>['content'];
	}

	let { content }: Props = $props();

	let {
		mobile_grid_start = '1',
		mobile_grid_end = '4',
		desktop_grid_start = '4',
		desktop_grid_end = '9'
	} = content;

	let gridClasses = $derived(
		`col-start-${mobile_grid_start} col-end-${+mobile_grid_end + 1} lg:col-start-${desktop_grid_start} lg:col-end-${+desktop_grid_end + 1}`
	);
</script>

<PageGrid class={content.spacing !== 'none' ? content.spacing : undefined}>
	<section use:editable={content} class="sb-section {gridClasses} text-{content.textColor}">
		<SbRichtext text={content.text} />
	</section>
</PageGrid>

<style lang="scss">
	section.sb-section {
		:global {
			h1,
			h2,
			h3,
			h4,
			h5,
			h6 {
				font-style: normal;
				font-weight: theme('fontWeight.semibold');
				line-height: 110%;

				&:not(:last-child) {
					margin-bottom: theme('spacing.4');

					@media (min-width: 1024px) {
						margin-bottom: theme('spacing.5');
					}
				}
			}

			h1 {
				font-size: 32px;
				letter-spacing: -0.32px;

				@media (min-width: 1024px) {
					font-size: 56px;
					letter-spacing: -0.56px;
				}
			}

			h2 {
				font-size: 20px;

				@media (min-width: 1024px) {
					font-size: 32px;
				}
			}

			h3 {
				font-size: 18px;

				@media (min-width: 1024px) {
					font-size: 28px;
				}
			}

			h4,
			h5,
			h6 {
				font-size: 16px;

				@media (min-width: 1024px) {
					font-size: 24px;
				}
			}

			ol,
			ul,
			p {
				&:not(:last-child) {
					margin-bottom: theme('spacing.4');

					@media (min-width: 1024px) {
						margin-bottom: theme('spacing.5');
					}
				}
			}

			ul,
			ol {
				padding-left: 20px;
			}

			p,
			ol li,
			ul li {
				font-size: 16px;
				line-height: 160%;

				@media (min-width: 1024px) {
					font-size: 20px;
				}
			}

			li p {
				display: inline;
			}

			a {
				text-decoration: underline;
				font-weight: theme('fontWeight.semibold');
			}

			ul li {
				list-style-type: disc;
			}

			ol li {
				list-style-type: decimal;
			}
		}
	}
</style>
